<template>
    <Sheet data-component="TodayViewFiltersModalSheet"
           class="today-view-filters-modal"
           size="sm"
           icon="fa-solid fa-sliders"
           :cancel="cancel"
           title="TODAY_VIEW_FILTERS.MODAL_TITLE">
        <form class="form-v"
              @submit.prevent="apply">
            <div v-if="!isOnline"
                 class="alert alert-warning">
                <i class="fas fa-info-circle"></i> {{ l('TODAY_VIEW_FILTERS.OFFLINE_ALERT') }}
            </div>

            <div class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.SOLD_OUT_LABEL') }}</label>
                </div>

                <div class="flex flex--gutter-gap flex--align-stretch">
                    <div class="btn-group-flex flex--align-stretch">
                        <button type="button"
                                data-testid="hideSoldOutFalse"
                                :class="{'active': !filters.hideSoldOut}"
                                class="btn btn-default btn-lg"
                                @click.prevent="filters.hideSoldOut = false">
                            {{ l('TODAY_VIEW_FILTERS.FORMS__SHOW_BTN') }}
                        </button>
                        <button type="button"
                                data-testid="hideSoldOutTrue"
                                :class="{'active': filters.hideSoldOut}"
                                class="btn btn-default btn-lg"
                                @click.prevent="filters.hideSoldOut = true">
                            {{ l('TODAY_VIEW_FILTERS.FORMS__HIDE_BTN') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.INACTIVE_LABEL') }}</label>
                </div>

                <div class="flex flex--gutter-gap flex--align-stretch">
                    <div class="btn-group-flex flex--align-stretch">
                        <button type="button"
                                data-testid="hideInactiveCategoriesAndEventsFalse"
                                :class="{'active': !filters.hideInactiveCategoriesAndEvents}"
                                class="btn btn-default  btn-lg"
                                @click.prevent="filters.hideInactiveCategoriesAndEvents = false">
                            {{ l('TODAY_VIEW_FILTERS.FORMS__SHOW_BTN') }}
                        </button>
                        <button type="button"
                                data-testid="hideInactiveCategoriesAndEventsTrue"
                                :class="{'active': filters.hideInactiveCategoriesAndEvents}"
                                class="btn btn-default btn-lg"
                                @click.prevent="filters.hideInactiveCategoriesAndEvents = true">
                            {{ l('TODAY_VIEW_FILTERS.FORMS__HIDE_BTN') }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="showItemBranchSelector"
                 class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.BRANCH_LABEL') }}</label>
                </div>

                <select v-model="filters.itemBranchID"
                        class="form-control input-lg">
                    <option v-for="branch in selectableBranches"
                            :key="branch.ID"
                            :value="branch.ID">
                        {{ branch.name }}
                    </option>
                </select>
            </div>

            <div class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.EVENT_LABEL') }}</label>
                    <span v-if="isEventsFilterDirty"
                          class="control-label__clear"
                          data-testid="clearSelectedEvents"
                          @click="clearSelectedEvents()">Clear</span>
                </div>
                <MultiSelect name="events"
                             :initial-options="sortedEvents"
                             :selected-option-ids="filters.eventIDs"
                             placeholder="TODAY_VIEW_FILTERS.MULTI_SELECT--PLACEHOLDER-EVENTS"
                             no-results-text="TODAY_VIEW_FILTERS.MULTI_SELECT--NO_RESULTS-EVENTS"
                             scrollable-container-selector=".today-view-filters-modal .sheet-body"
                             @update:selected-options="updateSelectedEvents" />
            </div>

            <div class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.CATEGORY_LABEL') }}</label>
                    <span v-if="isCategoriesFilterDirty"
                          class="control-label__clear"
                          data-testid="clearSelectedCategories"
                          @click="clearSelectedCategories()">Clear</span>
                </div>
                <MultiSelect name="categories"
                             :initial-options="sortedCategories"
                             :selected-option-ids="filters.categoryIDs"
                             placeholder="TODAY_VIEW_FILTERS.MULTI_SELECT--PLACEHOLDER-CATEGORIES"
                             no-results-text="TODAY_VIEW_FILTERS.MULTI_SELECT--NO_RESULTS-CATEGORIES"
                             scrollable-container-selector=".today-view-filters-modal .sheet-body"
                             @update:selected-options="updateSelectedCategories" />
            </div>

            <div class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.STATUS_LABEL') }}</label>
                </div>
                <select v-model="filters.status"
                        class="form-control input-lg">
                    <option :value="null">
                        {{ l('TODAY_VIEW_FILTERS.ALL_STATUSES') }}
                    </option>
                    <option value="Active">
                        {{ l('TODAY_VIEW_FILTERS.STATUS_ACTIVE') }}
                    </option>
                    <option value="Inactive">
                        {{ l('TODAY_VIEW_FILTERS.STATUS_INACTIVE') }}
                    </option>
                </select>
            </div>

            <div v-if="isOnline"
                 class="form-group">
                <div class="control-label">
                    <label>{{ l('TODAY_VIEW_FILTERS.SPACES_LABEL') }}</label>
                </div>

                <div class="flex flex--gutter-gap flex--align-stretch">
                    <div class="btn-group-flex flex--align-stretch">
                        <a class="btn btn-default btn--inline-flex height-100pc"
                           :class="{active: filters.spacesAvailableMode === SpacesAvailableModeEnum.MIN}"
                           @click.prevent="filters.spacesAvailableMode = SpacesAvailableModeEnum.MIN">
                            {{ l('TODAY_VIEW_FILTERS.SPACES_AT_LEAST') }}
                        </a>
                        <a class="btn btn-default btn--inline-flex height-100pc"
                           :class="{active: filters.spacesAvailableMode === SpacesAvailableModeEnum.EXACT}"
                           @click.prevent="filters.spacesAvailableMode = SpacesAvailableModeEnum.EXACT">
                            {{ l('TODAY_VIEW_FILTERS.SPACES_EXACTLY') }}
                        </a>
                    </div>

                    <input v-model.number="filters.spacesAvailable"
                           v-select-on-focus
                           type="number"
                           min="0"
                           class="form-control input-lg" />

                    <!-- Hidden on mobile because there's not enough space -->
                    <div class="btn-group-flex flex--align-stretch desktop-only">
                        <a class="btn btn-default btn--inline-flex height-100pc"
                           @click.prevent="filters.spacesAvailable = Math.max(filters.spacesAvailable - 1, 0)">
                            <i class="fa-solid fa-minus mr-0" />
                        </a>
                        <a class="btn btn-default btn--inline-flex height-100pc"
                           @click.prevent="filters.spacesAvailable += 1">
                            <i class="fa-solid fa-plus mr-0" />
                        </a>
                    </div>
                </div>
            </div>
        </form>

        <template v-slot:footer>
            <div class="sheet-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           color="white"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>

                <SquareBtn v-if="hasFiltersSelected"
                           size="lg"
                           color="white"
                           class="reset-btn"
                           @click="reset">
                    {{ l('RESET') }}
                </SquareBtn>

                <!-- Push the Reset button over to the left where it's harder to accidentally press -->
                <span class="flex--grow desktop-only"></span>

                <SquareBtn size="lg"
                           color="primary"
                           class="apply-btn"
                           @click="apply">
                    {{ l('APPLY') }}
                </SquareBtn>
            </div>
        </template>
    </Sheet>
</template>

<script>
import MultiSelect from '@/vue/components/Forms/MultiSelect';
import Sheet from '@/vue/components/Modals/Sheet';
import SquareBtn from '@/vue/components/SquareBtn';
import { cloneShallow } from '@/functions/clone';
import { container } from '@/container';
import { createDefaultFilterValues, getSelectedFilterKeys } from '@/epos/vue/stores/modules/todayStore';
import { mapState } from 'vuex';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { organizeByProperty } from '@/functions/arrayOrganizing';
import { selectOnFocus } from '@/vue/directives/selectOnFocus';
import { sortByName } from '@/functions/arraySorting';
import { SpacesAvailableModeEnum } from '@/epos/vue/pages/today/SpacesAvailableModeEnum';

/**
 * @typedef {import('./types.js').FilterValues} FilterValues
 */

export default {
    name: 'TodayViewFiltersModalSheet',

    components: {
        MultiSelect,
        Sheet,
        SquareBtn,
    },

    directives: {
        selectOnFocus,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            /**
             * We'll modify this object in the modal, then return it in the promise response.
             * That means changing anything in this modal doesn't make any changes until you press apply.
             * It's the responsibility of the code that opens the modal to handle the result and actually apply the
             * filters.
             *
             * @type {FilterValues}
             */
            filters: createDefaultFilterValues(),

            /**
             * @type {number[]|null}
             */
            selectedCategoryIDs: null,

            /**
             * @type {number[]|null}
             */
            selectedEventIDs: null,
        };
    },

    computed: {
        SpacesAvailableModeEnum() {
            return SpacesAvailableModeEnum;
        },
        ...mapState(['isOnline', 'currentBranch', 'currentItemBranch', 'currentUser']),

        sortedCategories() {
            let categories = Object.values(this.$store.state.today.categories);
            // Uncomment if we decide to make the dropdowns contextual again.
            // if (this.filters.hideInactiveCategoriesAndEvents) {
            //    categories = categories.filter((category) => category.status === 'Active');
            // }

            const catsByStatus = organizeByProperty(categories, 'status');

            // Sort the cats within the statuses by name.
            Object.keys(catsByStatus).forEach((key) => {
                sortByName(catsByStatus[key]);
            });

            return catsByStatus;
        },

        sortedEvents() {
            let events = Object.values(this.$store.state.today.events);

            // Uncomment if we decide to make the dropdowns contextual again.
            // if (this.filters.hideInactiveCategoriesAndEvents) {
            //    events = events.filter((event) => event.status === 'Active');
            // }

            const eventsByStatus = organizeByProperty(events, 'status');

            // Sort the events within the statuses by name.
            Object.keys(eventsByStatus).forEach((key) => {
                sortByName(eventsByStatus[key]);
            });

            return eventsByStatus;
        },

        hasFiltersSelected() {
            return getSelectedFilterKeys(this.filters).length > 0;
        },

        showItemBranchSelector() {
            return container.userService.canChangeItemBranch();
        },

        /**
         * @returns {Branch[]}
         */
        selectableBranches() {
            return this.currentUser.branches.filter((b) => b.isSelectable());
        },

        isEventsFilterDirty() {
            return this.filters.eventIDs && this.filters.eventIDs.length > 0;
        },

        isCategoriesFilterDirty() {
            return this.filters.categoryIDs && this.filters.categoryIDs.length > 0;
        },
    },

    created() {
        // When we first open the filter modal set the itemBranch.
        this.$store.state.today.filters.itemBranchID = this.currentItemBranch.ID;

        // Set the filters to the current values.
        this.setFilters(cloneShallow(this.$store.state.today.filters));
    },

    methods: {
        async apply() {
            // Close any open popovers on the linear view.
            this.$store.commit('today/setActiveLinearPopover', null);

            this.resolve(this.filters);
        },

        clearSelectedEvents() {
            this.filters.eventIDs = [];
        },

        updateSelectedEvents(selectedObjects) {
            if (!selectedObjects) {
                this.filters.eventIDs = [];
                return;
            }
            this.filters.eventIDs = selectedObjects.map((o) => o.ID);
        },

        clearSelectedCategories() {
            this.filters.categoryIDs = [];
        },

        updateSelectedCategories(selectedObjects) {
            if (!selectedObjects) {
                this.filters.categoryIDs = [];
                return;
            }
            this.filters.categoryIDs = selectedObjects.map((o) => o.ID);
        },

        reset() {
            this.setFilters(createDefaultFilterValues(
                {
                    itemBranchID: this.currentBranch.ID,
                }
            ));
        },

        setFilters(filters) {
            this.filters = filters;
        },
    },
};
</script>

<style lang="less" scoped>
@import "../../../../less/variables";
    .apply-btn {
        min-width: 150px;
    }
    .control-label__clear {
        font-size: 12px;
        text-decoration: underline;
        color: @dark-grey;
        cursor: pointer;
    }

    .form-v {
        // We need to force the form to the full height of the parent element .sheet-body otherwise
        // flex-grow: 1 on .sheet-body means that the height calculations for scrolling are wrong.
        min-height: 100%;
    }
</style>
